import type { TalkingPoint } from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { useMeetingPrepProductExclusions } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/MeetingPrepProductExclusionsContext";
import {
  hideableProductsOf,
  productNamesById,
  type HideableProduct,
} from "@acto/taskpane/field-sales-2/utils/productHide";
import { useHiddenProductViews } from "./useHiddenProductViews";
import type { HiddenProductsDisclosureProps } from "./HiddenProductsDisclosure";

/** `confirmRestore` defaults to the meeting prep's confirmation. */
export function useHiddenAmong(products: HideableProduct[], confirmRestore?: (message: string) => void) {
  const { exclusions, restore, restoringIds, confirm: confirmOnPrep } = useMeetingPrepProductExclusions();
  const confirm = confirmRestore ?? confirmOnPrep;
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");

  const names = productNamesById(products);
  const applicable = exclusions.filter((exclusion) => names.has(exclusion.productId));
  const hiddenIds = new Set(applicable.map((exclusion) => exclusion.productId));
  const hiddenViews = useHiddenProductViews(applicable, names);

  return {
    /** Naming no products is not the same as hiding all of them: an empty list is never hidden. */
    isHidden: (productIds: readonly string[]) => productIds.length > 0 && productIds.every((id) => hiddenIds.has(id)),
    disclosure: {
      products: hiddenViews,
      onRestore: (id: string) =>
        void restore(id).then((restored) => restored && confirm(t("restoredCount", { count: 1 }))),
      restoringIds,
    } satisfies HiddenProductsDisclosureProps,
  };
}

export const useHiddenOnCard = (point: TalkingPoint) => useHiddenAmong(hideableProductsOf(point));
